<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  active: Boolean
})

const bursting = ref(false)
let animationTimer = 0

async function play() {
  window.clearTimeout(animationTimer)
  bursting.value = false
  await nextTick()
  bursting.value = true
  animationTimer = window.setTimeout(() => {
    bursting.value = false
  }, 720)
}

watch(() => props.active, (active, previous) => {
  if (active && !previous) play()
})

onBeforeUnmount(() => window.clearTimeout(animationTimer))
</script>

<template>
  <span class="like-feedback" :class="{ 'is-bursting': bursting }">
    <span class="like-feedback__effect" aria-hidden="true">
      <i class="like-feedback__ring ring--one"></i>
      <i class="like-feedback__ring ring--two"></i>
      <i class="like-feedback__particle particle--one"></i>
      <i class="like-feedback__particle particle--two"></i>
      <i class="like-feedback__particle particle--three"></i>
      <i class="like-feedback__particle particle--four"></i>
      <i class="like-feedback__particle particle--five"></i>
      <i class="like-feedback__particle particle--six"></i>
    </span>
    <span class="like-feedback__content"><slot /></span>
  </span>
</template>

<style scoped>
.like-feedback { position: relative; display: grid; width: 100%; height: 100%; overflow: visible; place-items: center; pointer-events: none; }
.like-feedback__content { position: relative; z-index: 2; display: grid; place-items: center; }
.like-feedback__effect { position: absolute; z-index: 1; top: 50%; left: 50%; display: block; width: 48px; height: 48px; transform: translate(-50%,-50%); pointer-events: none; }
.like-feedback__ring,.like-feedback__particle { position: absolute; top: 50%; left: 50%; display: block; opacity: 0; pointer-events: none; }
.like-feedback__ring { width: 14px; height: 14px; margin: -7px 0 0 -7px; border: 1.5px solid #ff6b91; border-radius: 50%; }
.like-feedback__ring.ring--two { border-color: #6688ff; }
.like-feedback__particle { width: 5px; height: 5px; margin: -2.5px 0 0 -2.5px; border-radius: 50%; background: #ff5f88; }
.particle--two,.particle--five { background: #557cff; }
.particle--three { background: #9c62ff; }
.particle--four { background: #35cdd1; }
.particle--six { background: #ffb23f; }
.is-bursting .like-feedback__content { animation: viewer-like-pop .62s cubic-bezier(.16,.78,.28,1); }
.is-bursting .ring--one { animation: viewer-like-ring .58s ease-out; }
.is-bursting .ring--two { animation: viewer-like-ring .58s .08s ease-out; }
.is-bursting .particle--one { animation: viewer-like-particle-one .62s ease-out; }
.is-bursting .particle--two { animation: viewer-like-particle-two .65s .02s ease-out; }
.is-bursting .particle--three { animation: viewer-like-particle-three .6s .04s ease-out; }
.is-bursting .particle--four { animation: viewer-like-particle-four .64s ease-out; }
.is-bursting .particle--five { animation: viewer-like-particle-five .62s .03s ease-out; }
.is-bursting .particle--six { animation: viewer-like-particle-six .66s .01s ease-out; }
@keyframes viewer-like-pop { 0% { transform: scale(.72); } 38% { transform: scale(1.42); } 64% { transform: scale(.9); } 82% { transform: scale(1.08); } 100% { transform: scale(1); } }
@keyframes viewer-like-ring { 0% { transform: scale(.25); opacity: .9; } 100% { transform: scale(2.7); opacity: 0; } }
@keyframes viewer-like-particle-one { 0% { transform: translate(0,0) scale(.5); opacity: 1; } 100% { transform: translate(-18px,-18px) scale(0); opacity: 0; } }
@keyframes viewer-like-particle-two { 0% { transform: translate(0,0) scale(.6); opacity: 1; } 100% { transform: translate(1px,-25px) scale(0); opacity: 0; } }
@keyframes viewer-like-particle-three { 0% { transform: translate(0,0) scale(.5); opacity: 1; } 100% { transform: translate(20px,-16px) scale(0); opacity: 0; } }
@keyframes viewer-like-particle-four { 0% { transform: translate(0,0) scale(.6); opacity: 1; } 100% { transform: translate(23px,7px) scale(0); opacity: 0; } }
@keyframes viewer-like-particle-five { 0% { transform: translate(0,0) scale(.5); opacity: 1; } 100% { transform: translate(-20px,10px) scale(0); opacity: 0; } }
@keyframes viewer-like-particle-six { 0% { transform: translate(0,0) scale(.6); opacity: 1; } 100% { transform: translate(8px,23px) scale(0); opacity: 0; } }
@media (prefers-reduced-motion: reduce) {
  .is-bursting .like-feedback__content,.is-bursting .like-feedback__ring,.is-bursting .like-feedback__particle { animation: none; }
}
</style>
